import { useMemo } from "react"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { PatientContextAppointmentFragment$data } from "@/modules/patients/services/__generated__/PatientContextAppointmentFragment.graphql"
import { usePatientPrevention } from "@/modules/patients/services/usePatientPrevention"
import { PointOfCareType, PreventionProgramTreatmentPlanTemplateCase } from "@/types/__generated__/union-types"

export interface PreventionProgramDetails {
  id: string
  name: { en: string; de: string }
  patientId: string
  key: string
  lastUpdated?: string | null
  localizedInstruction: { de: string; en: string }
  pointOfCare: PointOfCareType
  matchedCase: PreventionProgramTreatmentPlanTemplateCase
  treatmentPlanTemplateId: string | null
}

export const useAppointmentPreventionPrograms = (
  appointmentContext: PatientContextAppointmentFragment$data,
  patientId: string,
): PreventionProgramDetails[] => {
  const country = useCountryParam()
  const { preventionPrograms: patientPreventionPrograms } = usePatientPrevention(patientId, country)

  const displayablePrograms = useMemo(() => {
    if (!appointmentContext.relatedPreventionPrograms.length || !patientPreventionPrograms) {
      return []
    }

    const preventionPrograms: PreventionProgramDetails[] = []

    appointmentContext.relatedPreventionPrograms.forEach((program) => {
      const patientProgram = (patientPreventionPrograms.edges ?? []).find(
        (p) => p.node.key === program.key && p.node.status === "BOOKED",
      )?.node

      if (patientProgram) {
        const { analysis, key, name, patientId, preventionProgramId } = program

        preventionPrograms.push({
          id: preventionProgramId,
          name,
          patientId,
          key,
          localizedInstruction: patientProgram.localizedInstruction,
          lastUpdated: patientProgram.lastUpdated,
          pointOfCare: analysis.pointOfCare,
          matchedCase: analysis.matchedCase,
          treatmentPlanTemplateId: analysis.treatmentPlanTemplateId,
        })
      }
    })

    return preventionPrograms
  }, [appointmentContext.relatedPreventionPrograms, patientPreventionPrograms])

  return displayablePrograms
}
